import React, { useState } from "react";

import { Box, Stack, SxProps, Typography, useMediaQuery, useTheme } from "@mui/material";

import { useTranslation } from "react-i18next";

import { Pie, PieChart, ResponsiveContainer } from "recharts";

import { CategoryArcsSettingsStateType, defaultSettings } from "./CategoryArcsSettings";
import TitleCase from "components/formatting/TitleCase";

interface CategoryBarPreviewProps {
  /**
   * State object represented by series of key value pairs,
   * where the key is a string which represents the title of the
   * updatable settings option.
   */
  settingsState: CategoryArcsSettingsStateType | {};
}

export default function CategoryArcsPreview(props: CategoryBarPreviewProps) {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const windowHeight = window.innerHeight;
  const chartColors = theme.colors.namedColors.chartColorArray;
  const currentState = {...defaultSettings, ...props.settingsState};
  const [chartSize, setChartSize] = useState<number>(100);

  let dummyData = [
    [{ name: i18n.t("one"), value: 90, fill: chartColors[0] }],
    [{ name: i18n.t("two"), value: 50, fill: chartColors[0] }],
    [{ name: i18n.t("three"), value: 25, fill: chartColors[0] }],
    [{ name: i18n.t("four"), value: 20, fill: chartColors[0] }],
    [{ name: i18n.t("five"), value: 10, fill: chartColors[0] }],
    [{ name: i18n.t("six"), value: 60, fill: chartColors[0] }],
    [{ name: i18n.t("seven"), value: 35, fill: chartColors[0] }]
  ];

  dummyData = dummyData.map(item => {
    return [
      ...item,
      { 
        name: i18n.t("other"),
        value: 100 - item[0].value,
        fill: 'gray'
      }
    ]
  });

  const arcWidths: Record<string, string> = { s: '75px', m: '100px', l: '175px' };

  let arcContainerStyle: SxProps = {
    display: 'grid',
    gridTemplateColumns: `repeat(${currentState.columns ?? 3}, 1fr)`,
    overflowY: 'auto',
    p: 2,
    pt: currentState.widgetTitle === 'none' ? 2 : 0
  };

  if (currentState.layout === 'h') {
    arcContainerStyle.display = 'flex';
    arcContainerStyle.flexWrap = 'wrap';
    arcContainerStyle.justifyContent = 'space-around';
  } else if (currentState.layout === 'v') {
    arcContainerStyle = { p: 0, gridTemplateRows: 'auto 1fr', textAlign: 'center' };
  }

  return (
    <Stack
      sx={{
        gap: 1,
        flex: 1,
        height: windowHeight < 600 ? "35vh" : "55vh",
        overflow: "hidden",
        width: isSmallScreen ? "100%" : "50%"
      }}>
      {currentState.widgetTitle !== 'none' &&
        <Typography
          sx={{ fontSize: "1.2rem", textAlign: `${currentState.widgetTitleAlignment}` }}
          variant="subtitle2">
          <TitleCase text={currentState.widgetTitle} />
        </Typography>}
      <Box sx={arcContainerStyle}>
        {dummyData.map(dataItem => {
          return (
            <Box
              key={dataItem[0].name}
              sx={{
                textAlign: "center",
                maxHeight: "100%",
                position: "relative",
                width: arcWidths[currentState.arcSize]
              }}>
              <Typography>{dataItem[0].name}</Typography>
              <ResponsiveContainer
                aspect={1}
                height="100%"
                onResize={(width, height) => {
                  setChartSize(Math.min(width, height));
                }}>
                <PieChart>
                  <Pie
                    cx="50%" 
                    cy="50%" 
                    data={dataItem}
                    dataKey="value"
                    endAngle={450}
                    innerRadius="60%"
                    nameKey="name"
                    outerRadius="90%"
                    startAngle={90} />
                  <text
                    dy={chartSize * 0.06}
                    style={{ fontSize: chartSize * 0.18, fontWeight: 'bold', fill: dataItem[0].fill }}
                    textAnchor='middle'
                    x='50%'
                    y='50%'
                  >
                    {dataItem[0].value + "%"}
                  </text>
                </PieChart>
              </ResponsiveContainer>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}